/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement } from 'react';
import { DynamicImage } from '@/components/dynamic-image';
import type { DynamicImageDimensions } from '@/lib/images/dynamic-image';
import { cn } from '@/lib/utils';
import type { Image } from '@/types';

/** Page Designer focal point (percentages) to a CSS `object-position`, centered by default. */
function objectPosition(image: Image): string {
    const x = image.focalPoint?.x != null ? `${image.focalPoint.x}%` : '50%';
    const y = image.focalPoint?.y != null ? `${image.focalPoint.y}%` : '50%';
    return `${x} ${y}`;
}

interface ResponsiveBannerImageProps {
    /** Desktop image (also used on mobile when `mobileImage` is empty). */
    image?: Image;
    /** Optional art-directed image for mobile (< md). */
    mobileImage?: Image;
    alt?: string;
    /** DIS widths per breakpoint, see `DynamicImage`. */
    widths: DynamicImageDimensions;
    priority?: 'high' | 'auto';
    loading?: 'eager' | 'lazy';
    /** Applied to each image wrapper; must give it a size (e.g. `absolute inset-0 size-full`). */
    className?: string;
}

/**
 * Cover image with an optional mobile variant and the Page Designer focal point, shared by the
 * PriceShoes banner components (Banner, Split Hero). When both images are set, each one is hidden
 * on the other breakpoint, so a lazy image is only fetched where it is visible.
 */
export function ResponsiveBannerImage({
    image,
    mobileImage,
    alt,
    widths,
    priority = 'auto',
    loading = 'lazy',
    className,
}: ResponsiveBannerImageProps): ReactElement | null {
    if (!image?.url && !mobileImage?.url) return null;

    return (
        <>
            {image?.url && (
                <DynamicImage
                    src={image.url}
                    alt={alt || ''}
                    widths={widths}
                    priority={priority}
                    loading={loading}
                    className={cn(className, mobileImage?.url && 'hidden md:block')}
                    imageProps={{
                        className: 'size-full object-cover',
                        style: { objectPosition: objectPosition(image) },
                    }}
                />
            )}
            {mobileImage?.url && (
                <DynamicImage
                    src={mobileImage.url}
                    alt={alt || ''}
                    widths={widths}
                    priority={priority}
                    loading={loading}
                    className={cn(className, image?.url && 'md:hidden')}
                    imageProps={{
                        className: 'size-full object-cover',
                        style: { objectPosition: objectPosition(mobileImage) },
                    }}
                />
            )}
        </>
    );
}
